import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { CalendarDays } from "lucide-react";

/**
 * DateInput: displays dates as DD/MM/YYYY (Italian format)
 * but stores/returns ISO format (YYYY-MM-DD) for API compatibility.
 */
export function DateInput({ value, onChange, disabled, className = "", ...props }) {
  const [display, setDisplay] = useState("");
  const hiddenRef = useRef(null);

  // Sync display from ISO value
  useEffect(() => {
    if (!value) { setDisplay(""); return; }
    // value is YYYY-MM-DD
    const parts = value.split("-");
    if (parts.length === 3) {
      setDisplay(`${parts[2]}/${parts[1]}/${parts[0]}`);
    }
  }, [value]);

  const handleDisplayChange = (e) => {
    let raw = e.target.value.replace(/[^\d/]/g, "");

    // Auto-insert slashes
    const digits = raw.replace(/\//g, "");
    if (digits.length <= 2) {
      raw = digits;
    } else if (digits.length <= 4) {
      raw = digits.slice(0, 2) + "/" + digits.slice(2);
    } else {
      raw = digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4, 8);
    }

    setDisplay(raw);

    // Parse complete date DD/MM/YYYY → YYYY-MM-DD
    const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      const iso = `${yyyy}-${mm}-${dd}`;
      // Basic validation
      const d = new Date(iso);
      if (!isNaN(d.getTime()) && d.getDate() === parseInt(dd)) {
        onChange?.({ target: { value: iso } });
      }
    } else if (raw === "") {
      onChange?.({ target: { value: "" } });
    }
  };

  const openNativePicker = () => {
    if (disabled) return;
    hiddenRef.current?.showPicker?.();
  };

  const handleNativeChange = (e) => {
    onChange?.(e);
  };

  return (
    <div className="relative">
      <Input
        {...props}
        type="text"
        inputMode="numeric"
        placeholder="GG/MM/AAAA"
        value={display}
        onChange={handleDisplayChange}
        disabled={disabled}
        className={`pr-9 ${className}`}
        maxLength={10}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={openNativePicker}
        disabled={disabled}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
      >
        <CalendarDays className="w-4 h-4" />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value || ""}
        onChange={handleNativeChange}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
    </div>
  );
}
